import { useNavigate } from '@exam-center/router';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Form,
  FormTextarea,
  Text,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import { FormProvider, useForm } from 'react-hook-form';

import { useRetireExamCenterRoom } from '../../hooks/use-retire-exam-center-room';

import { type RetireExamCenterRoomFormInput, RetireExamCenterRoomFormSchema } from './schema';

interface RetireExamCenterRoomModalProps {
  open: boolean;
  onClose: () => void;
  roomId: string;
}

export default function RetireExamCenterRoomModal(props: Readonly<RetireExamCenterRoomModalProps>) {
  const { open: isOpen, onClose, roomId } = props;

  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const formContext = useForm<RetireExamCenterRoomFormInput>({
    resolver: zodResolver(RetireExamCenterRoomFormSchema),
    defaultValues: { reason: '' },
  });

  const { handleSubmit, reset, formState } = formContext;

  const [retireRoom, { loading }] = useRetireExamCenterRoom();

  const handleRetire = async (data: RetireExamCenterRoomFormInput) => {
    try {
      await retireRoom({ roomId, remark: data.reason });
      showSnackbar({ message: 'ยกเลิกการใช้งานสถานที่สอบเรียบร้อยแล้ว', variant: 'success' });
      onClose();
      navigate('/admin/exam-location/cancellations');
    } catch {
      showSnackbar({
        message: 'ยกเลิกการใช้งานสถานที่สอบไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
        variant: 'error',
      });
    }
  };

  return (
    <FormProvider {...formContext}>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth='sm'
        fullWidth
        disableEscapeKeyDown={formState.isSubmitting}
        slotProps={{
          paper: {
            component: Form,
            onSubmit: handleSubmit(handleRetire),
          },
          transition: {
            onExited: () => reset(),
          },
        }}
      >
        <DialogTitle onClose={onClose} loading={formState.isSubmitting}>
          ยกเลิกการใช้งานสถานที่สอบ?
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ display: 'flex', flexDirection: 'column', gap: (theme) => theme.spacing(4) }}
        >
          <Text>
            เมื่อทำการยกเลิกแล้ว{' '}
            <Text component='span' sx={{ fontWeight: 'bold' }}>
              จะไม่สามารถใช้งานสถานที่สอบนี้ในการจัดสอบได้อีก
            </Text>{' '}
            กรุณาตรวจสอบความถูกต้องให้เรียบร้อยก่อนดำเนินการต่อ
          </Text>

          <FormTextarea
            name='reason'
            label='กรุณาระบุสาเหตุที่ยกเลิก'
            required
            fullWidth
            minRows={1}
            placeholder='กรุณาระบุสาเหตุที่ยกเลิก'
          />
        </DialogContent>
        <DialogActions>
          <Button
            size='large'
            variant='outlined'
            color='secondary'
            onClick={onClose}
            disabled={formState.isSubmitting || loading}
          >
            ยกเลิก
          </Button>
          <Button
            size='large'
            type='submit'
            color='error'
            loading={formState.isSubmitting || loading}
          >
            ยืนยันยกเลิกการใช้งาน
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
